/**
 * WarmingUpScreen — shown while the backend cold-starts.
 *
 * The Render free tier spins down after 15 min inactivity.
 * Cold start takes ~30–50 s. Status prop drives progressive messaging.
 */

export type BootStatus = 'booting' | 'warming' | 'error' | 'unavailable'

interface Props {
  status: BootStatus
}

export default function WarmingUpScreen({ status }: Props) {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-4">
        {status !== 'error' && status !== 'unavailable' && (
          <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-700 rounded-full animate-spin" />
        )}
        <div className="text-center space-y-1">
          {status === 'booting' && (
            <>
              <p className="text-stone-800 font-medium text-sm">Starting up…</p>
              <p className="text-stone-400 text-xs">Just a moment.</p>
            </>
          )}
          {status === 'warming' && (
            <>
              <p className="text-stone-800 font-medium text-sm">Waking up the server</p>
              <p className="text-stone-400 text-xs">
                This can take ~30 seconds on first load.
              </p>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-stone-800 font-medium text-sm">
                This is taking longer than expected
              </p>
              <p className="text-stone-400 text-xs">Please refresh the page to try again.</p>
            </>
          )}
          {status === 'unavailable' && (
            <>
              <p className="text-stone-800 font-medium text-sm">Temporarily unavailable</p>
              <p className="text-stone-400 text-xs">
                Your records are safe. This page will continue on its own once the service is
                back.
              </p>
            </>
          )}
        </div>
      </div>

      <footer className="absolute bottom-4 text-xs text-stone-400">
        <a href="/privacy" className="hover:text-stone-600 transition-colors">
          Privacy
        </a>
      </footer>
    </div>
  )
}
